import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import AllRequestsTable from './AllRequestsTable';
import AllRequestsForm from './AllRequestsForm';
import StatusChangeDialog from './StatusChangeDialog';
import RequestCommentHistoryDialog from './RequestCommentHistoryDialog';
import useAllRequestsData from '../hooks/useAllRequestsData';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLmsPermission } from '../../hooks/useLmsPermission';
import { LMS } from '../../../../constants/permissionCodes';
import useLmsCodeOptions from '../../hooks/useLmsCodeOptions';
import { formatErrorMessage } from '../../../../utils/errorUtils';

/**
 * "Tüm Talepler" sekmesi — onay akışı (bkz. docs/talep_olustur_onay_akısı.md). Her iki
 * `requestType`'ı (Yayınlanan/Talep Edilen) statüsüyle birlikte tek listede gösterir; statü
 * geçişlerini (Onaya Gönder/Kabul/Red/İade) ve yorum geçmişini yönetir. `PublishedTrainingsTab.jsx`
 * / `RequestedTrainingsTab.jsx`'e ve onların tablo/form bileşenlerine hiçbir şey eklenmedi/
 * çıkarılmadı — bu tamamen ayrı, kendi kendine yeten bir sekme.
 */
const AllRequestsTab = () => {
    const { th } = useLanguage();
    const { can } = useLmsPermission();
    const [selectedItem, setSelectedItem] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [statusChangeContext, setStatusChangeContext] = useState(null); // { row, targetStatus }
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);
    const [historyContext, setHistoryContext] = useState(null); // { requestId, requestType }
    const toast = useRef(null);

    const {
        trainingRequestApi,
        isLoading,
        lazyParams,
        onPage,
        saveRequest,
        activeFilters,
        onFiltersChange,
        initFilters,
        sortField,
        sortOrder,
        onSortChange,
        deleteRequest,
        changeStatus,
        refetch
    } = useAllRequestsData();

    const { options: vehicleModelOptions } = useLmsCodeOptions('VEHICLE_MODEL');
    const { options: trainingTypeOptions } = useLmsCodeOptions('TRAINING_TYPES');
    const { options: trainingLocationOptions } = useLmsCodeOptions('TRAINING_LOCATION');

    const handleEdit = (rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    };

    const handleCreateClick = () => {
        setSelectedItem(null);
        setDialogVisible(true);
    };

    const handleHide = () => {
        setDialogVisible(false);
        setSelectedItem(null);
    };

    const handleSubmit = async (formData) => {
        const success = await saveRequest(formData);
        if (success) {
            handleHide();
            refetch?.();
        }
    };

    const handleDelete = (id) => {
        if (!id) return;
        confirmDialog({
            message: th('trainingRequest.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                // bkz. RequestedTrainingsTab.jsx'teki aynı sıralama notu (dialog önce kapatılır).
                handleHide();
                try {
                    await deleteRequest(id);
                    toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('trainingRequest.deleteSuccess'), life: 3000 });
                    refetch?.();
                } catch {
                    toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('trainingRequest.deleteError'), life: 4000 });
                }
            }
        });
    };

    const handleStatusAction = (row, targetStatus) => setStatusChangeContext({ row, targetStatus });
    const handleStatusChangeHide = () => {
        if (statusChangeLoading) return;
        setStatusChangeContext(null);
    };

    const handleStatusChangeConfirm = async (comment) => {
        if (!statusChangeContext) return;
        const { row, targetStatus } = statusChangeContext;
        setStatusChangeLoading(true);
        try {
            // BE her iki uçta da (ChangeStatus/GetComments) requestType'ı zorunlu kılıyor (bkz.
            // [[project_tender_request_screen]] BE verification pass) — satırın kendi requestType'ı
            // (1=Yayınlanan, 2=Talep Edilen) burada iletiliyor.
            await changeStatus({ id: row.requestId ?? row.id, newStatus: targetStatus, comment, requestType: row.requestType });
            toast.current?.show({ severity: 'success', summary: th('common.success'), detail: th('trainingRequest.allRequests.statusChange.success'), life: 3000 });
            setStatusChangeContext(null);
            refetch?.();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: formatErrorMessage(err) || th('trainingRequest.allRequests.statusChange.error'), life: 4000 });
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleShowHistory = (row) => {
        const requestId = row.requestId ?? row.id ?? null;
        setHistoryContext(requestId != null ? { requestId, requestType: row.requestType } : null);
    };
    const handleHideHistory = () => setHistoryContext(null);

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-end align-items-center mb-4 gap-2">
                <Button label={th('filter.reset')} icon="pi pi-filter-slash" outlined onClick={initFilters} />
                {can(LMS.REQUEST_CREATE) && <Button label={th('trainingRequest.newButton')} icon="pi pi-plus" onClick={handleCreateClick} />}
            </div>

            <AllRequestsTable
                data={trainingRequestApi}
                totalCount={trainingRequestApi?.totalCount}
                loading={isLoading}
                lazyParams={lazyParams}
                onPage={onPage}
                onEdit={handleEdit}
                activeFilters={activeFilters}
                onFiltersChange={onFiltersChange}
                vehicleModelOptions={vehicleModelOptions}
                trainingTypeOptions={trainingTypeOptions}
                trainingLocationOptions={trainingLocationOptions}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSortChange}
                onShowHistory={handleShowHistory}
            />

            {dialogVisible && (
                <AllRequestsForm
                    visible={dialogVisible}
                    onHide={handleHide}
                    onSubmit={handleSubmit}
                    initialData={selectedItem}
                    loading={isLoading}
                    handleDelete={can(LMS.REQUEST_DELETE) ? handleDelete : undefined}
                    readOnly={!!selectedItem && !can(LMS.REQUEST_UPDATE)}
                    // Onay/red ile güncelleme ayrımı iş birimi kararını bekliyor; BE ikisinden birini kabul ediyor
                    onStatusAction={can([LMS.REQUEST_UPDATE, LMS.REQUEST_APPROVE]) ? handleStatusAction : undefined}
                />
            )}

            {statusChangeContext && (
                <StatusChangeDialog visible={!!statusChangeContext} targetStatus={statusChangeContext.targetStatus} onHide={handleStatusChangeHide} onConfirm={handleStatusChangeConfirm} loading={statusChangeLoading} />
            )}

            {historyContext != null && <RequestCommentHistoryDialog visible={historyContext != null} requestId={historyContext.requestId} requestType={historyContext.requestType} onHide={handleHideHistory} />}
        </div>
    );
};

export default AllRequestsTab;
